import React, { useCallback, useMemo, useRef } from 'react';
import clsx from 'clsx';
import styles from './styles/base.module.css';
/**
 * `<TabList/>`представляет собой набор компонентов для организации табов.
 *
 * @example ``` jsx
 *    <TabList active={active} onChangeTab={(_, tab) => setActive(tab)}>
 *      <Tab>Tab 1</Tab>
 *      <Tab>Tab 2</Tab>
 *      <Tab>Tab 3</Tab>
 *    </TabList>
 * ```
 */
export const TabList = props => {
  const {
    children,
    className,
    active,
    onChangeTab,
    tabIndex = -1,
    size = 'l',
    ...otherProps
  } = props;
  const classNames = useMemo(() => clsx(styles.tabList, className, styles[size]), [className, size]);
  const currentFocused = useRef(active);
  const onFocusTab = useCallback((e, index) => {
    currentFocused.current = index;
  }, [currentFocused]);
  const onSelectTab = useCallback((e, newTab) => {
    if (newTab !== active) {
      onChangeTab(e, newTab);
      currentFocused.current = newTab;
    }
  }, [active, onChangeTab]);
  const [tabs, indices, refs] = useMemo(() => {
    const localTabs = [];
    const localIndices = [];
    const localRefs = [];
    React.Children.forEach(children, (child, index) => {
      if (! /*#__PURE__*/React.isValidElement(child)) {
        return;
      }
      const ref = /*#__PURE__*/React.createRef();
      localTabs.push(/*#__PURE__*/React.cloneElement(child, {
        tabIndex: index,
        key: child.key || `tab-${index}`,
        onClick: onSelectTab,
        onFocus: onFocusTab,
        active: active === index,
        className: clsx(styles.tab),
        size,
        ref
      }));
      if (!child.props.disabled) {
        localIndices.push(index);
        localRefs.push(ref);
      }
    });
    return [localTabs, localIndices, localRefs];
  }, [children, onSelectTab, onFocusTab, active, size]);
  const onKeyDown = useCallback(e => {
    const eventKeyCode = e.keyCode;
    switch (eventKeyCode) {
      case 13:
        e.preventDefault();
        onSelectTab(e, currentFocused.current);
        break;
      case 37:
        {
          e.preventDefault();
          const next = (indices.indexOf(currentFocused.current) + indices.length - 1) % indices.length;
          const el = refs[next];
          if (el && el.current) {
            el.current.focus();
          }
        }
        break;
      case 39:
        {
          e.preventDefault();
          const next = (indices.indexOf(currentFocused.current) + 1) % indices.length;
          const el = refs[next];
          if (el && el.current) {
            el.current.focus();
          }
        }
        break;
    }
  }, [indices, onSelectTab, refs]);
  return /*#__PURE__*/React.createElement("div", {
    role: "tablist",
    tabIndex: tabIndex,
    className: classNames,
    onKeyDown: onKeyDown,
    ...otherProps
  }, tabs);
};